// src/redux/defectsSlice.ts
// 不良品情報
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import apiAxios from "../utils/apiAxios";
import { AnyAction, Dispatch } from "redux";
import { appendAlert } from "../redux/alertSlice";
import { ReviewsActionType } from "../models/PostDefectReportReviews";
import { GetDefectsReport } from "../models/GetDefectsReport";
import { PostDefectReportReviews } from "../models/PostDefectReportReviews";
import { PostDefectReport } from "../models/PostDefectReport";
import cloneDeep from "lodash/cloneDeep";

/**
 * 不良情報一覧のデータを表します。
 *
 * @property rows - 不良品実績
 * @property level1State - レベル1reviewerの承認状態
 * @property level1ReviewerNames - レベル1reviewerの名前
 * @property level1ReviewerIds - レベル1reviewerのID(=社員コード)
 * @property level1Comment - レベル1reviewerのコメント
 * @property level2State - レベル2reviewerの承認状態
 * @property level2ReviewerNames - レベル2reviewerの名前
 * @property level2ReviewerIds - レベル2reviewerのID(=社員コード)
 * @property level2Comment - レベル2reviewerの承認状態
 * @property comment - コメント
 * @property root_cause - 発生原因
 * @property corrective_action - 対策
 * @property review_seq - レビューシーケンス
 * @property inputErrors - エラーメッセージ
 */
export type DefectsList = {
  rows: GetDefectsReport[];
  level1State: string;
  level1ReviewerNames: string[];
  level1ReviewerIds: string[];
  level1Comment: string;
  level2State: string;
  level2ReviewerNames: string[];
  level2ReviewerIds: string[];
  level2Comment: string;
  comment: string;
  root_cause: string;
  corrective_action: string;
  review_seq: number;
  inputErrors: string[]; // エラーメッセージ
};

const initialState: DefectsList = {
  rows: [],
  level1State: "",
  level1ReviewerNames: [],
  level1ReviewerIds: [],
  level1Comment: "",
  level2State: "",
  level2ReviewerNames: [],
  level2ReviewerIds: [],
  level2Comment: "",
  comment: "",
  root_cause: "",
  corrective_action: "",
  review_seq: 0,
  inputErrors: [],
};

export const defectsListSlice = createSlice({
  name: "defects",
  initialState,
  reducers: {
    setDefectsList: (state, action: PayloadAction<GetDefectsReport[]>) => {
      state.rows = action.payload;
    },
    setDefects: (
      state,
      action: PayloadAction<{ defectsReport: GetDefectsReport; index: number }>
    ) => {
      state.rows = cloneDeep(state.rows);
      state.rows[action.payload.index] = cloneDeep(
        action.payload.defectsReport
      );
    },
    initApproval: (state, action: PayloadAction<GetDefectsReport>) => {
      state.inputErrors = [];
      state.root_cause = action.payload.root_cause;
      state.corrective_action = action.payload.corrective_action;
      const level1 = action.payload.review_history
        .filter((d) => d.reviewer_level === 1)
        .sort((a, b) => (a.review_seq > b.review_seq ? -1 : 1));
      state.level1Comment = level1[0].comments;
      state.level1State = level1[0].review_status;
      // 承認済みの場合、承認者の名前のみ、それ以外の場合はレビュアー全員の名前を画面に表示する
      if (state.level1State === "approve") {
        state.level1ReviewerNames = [level1[0].reviewer_name];
        state.level1ReviewerIds = [level1[0].reviewer_id];
      } else {
        state.level1ReviewerNames = Array.from(
          new Set(level1.map((d) => d.reviewer_name))
        );
        state.level1ReviewerIds = Array.from(
          new Set(level1.map((d) => d.reviewer_id))
        );
      }
      const level2 = action.payload.review_history
        .filter((d) => d.reviewer_level === 2)
        .sort((a, b) => (a.review_seq > b.review_seq ? -1 : 1));
      state.level2Comment = level2[0].comments;
      state.level2State = level2[0].review_status;
      if (state.level2State === "approve") {
        state.level2ReviewerNames = [level2[0].reviewer_name];
        state.level2ReviewerIds = [level2[0].reviewer_id];
      } else {
        state.level2ReviewerNames = Array.from(
          new Set(level2.map((d) => d.reviewer_name))
        );
        state.level2ReviewerIds = Array.from(
          new Set(level2.map((d) => d.reviewer_id))
        );
      }
      state.comment = "";
    },
    setReviewComment: (state, action: PayloadAction<string>) => {
      state.comment = action.payload;
    },
    setRootCause: (state, action: PayloadAction<string>) => {
      state.root_cause = action.payload;
    },
    setCorrectiveAction: (state, action: PayloadAction<string>) => {
      state.corrective_action = action.payload;
    },
    setInputErrors: (state, action: PayloadAction<string[]>) => {
      state.inputErrors = action.payload;
    },
  },
});

const {
  setDefectsList,
  setDefects,
  setReviewComment,
  initApproval,
  setRootCause,
  setCorrectiveAction,
  setInputErrors,
} = defectsListSlice.actions;

// 不良情報取得
// 本来は工場・加工・機械をパラメータにするはず
const fetchDefects = () => {
  return async (dispatch: Dispatch<AnyAction>) => {
    try {
      const response = await apiAxios.get<GetDefectsReport[]>(
        `/defect_reports`
      );
      const data = response.data;
      dispatch(setDefectsList(data));
    } catch (error: any) {
      console.error(error);
      dispatch(
        appendAlert({
          severity: "error",
          message: "データの取得に失敗しました。" + error.message,
          displayTime: 5000,
        })
      );
    }
  };
};

// 承認処理
const postDefectsReportReviews = (
  index: number,
  defectsReport: GetDefectsReport,
  action: ReviewsActionType,
  comment: string,
  root_cause: string,
  corrective_action: string,
  userId: string,
  reviewLevel: number
) => {
  return async (dispatch: Dispatch<AnyAction>) => {
    // 入力チェック
    const isError = validateDefects(
      dispatch,
      root_cause,
      corrective_action,
      comment
    );
    if (!isError) {
      // 発生原因・対策の更新 : 取得した内容から更新があった場合のみ
      if (
        defectsReport.corrective_action !== corrective_action ||
        defectsReport.root_cause !== root_cause
      ) {
        // POST Defect用データ
        const defectsBodys = createPostDefectsData(
          defectsReport,
          root_cause,
          corrective_action,
          userId
        );
        console.log(defectsBodys);
        try {
          const response = await apiAxios.post<PostDefectReport>(
            "/defect_reports",
            defectsBodys
          );
          console.log(response);
        } catch (error: any) {
          dispatch(
            appendAlert({
              severity: "error",
              message: "不良実績登録エラー " + error.message,
              displayTime: 5000,
            })
          );
        }
        dispatch(
          appendAlert({
            severity: "success",
            message: "発生原因・対策を更新しました。",
            displayTime: 5000,
          })
        );
      }

      // 不良不足報告書の承認
      const defectsReviewBody = createPostBody(
        defectsReport,
        userId,
        action,
        comment,
        reviewLevel
      );
      console.log(defectsReviewBody);
      try {
        const response = await apiAxios.post<PostDefectReportReviews>(
          "/defect_report_reviews",
          defectsReviewBody
        );
        console.log(response);
      } catch (error: any) {
        dispatch(
          appendAlert({
            severity: "error",
            message: "不良不足報告書の承認エラー " + error.message,
            displayTime: 5000,
          })
        );
      }

      // データの再取得
      try {
        const response = await apiAxios.get<GetDefectsReport[]>(
          `/defect_reports?&po_data_num=${defectsReport.po_data_num}&po_desc_num=${defectsReport.po_desc_num}&mach_seq=${defectsReport.mach_seq}&failure_count=${defectsReport.failure_count}&defect_type_seq=${defectsReport.defect_type_seq}`
        );
        dispatch(setDefects({ defectsReport: response.data[0], index: index }));
        dispatch(initApproval(response.data[0]));
      } catch (error: any) {
        dispatch(
          appendAlert({
            severity: "error",
            message: "更新処理後のデータ再取得エラー" + error.message,
            displayTime: 5000,
          })
        );
      }

      // 処理結果の通知
      if (action === "approve") {
        dispatch(
          appendAlert({
            severity: "success",
            message: "承認しました。",
            displayTime: 5000,
          })
        );
      } else {
        dispatch(
          appendAlert({
            severity: "success",
            message: "差し戻ししました。",
            displayTime: 5000,
          })
        );
      }
    }
  };
};

// 承認処理body
const createPostBody = (
  defectsReport: GetDefectsReport,
  reviewer_id: string,
  action: ReviewsActionType,
  comment: string,
  reviewLevel: number
) => {
  return [
    {
      po_data_num: defectsReport.po_data_num,
      po_desc_num: defectsReport.po_desc_num,
      mach_seq: defectsReport.mach_seq,
      failure_count: defectsReport.failure_count,
      defect_type_seq: defectsReport.defect_type_seq,
      reviewer_id: reviewer_id,
      reviewer_level: reviewLevel,
      action: action,
      comments: comment,
      update_by: reviewer_id,
    },
  ];
};

// 不良情報登録:発生原因、対策更新用
const createPostDefectsData = (
  defects: GetDefectsReport,
  root_cause: string,
  corrective_action: string,
  userId: string
): PostDefectReport[] => {
  const postDefectsData: PostDefectReport[] = [];

  postDefectsData.push({
    po_data_num: defects.po_data_num,
    po_desc_num: defects.po_desc_num,
    rev: Number(defects.rev),
    plant_code: defects.plant_code,
    mach_seq: defects.mach_seq,
    registering_proc_code: defects.registering_proc_code,
    registering_mach_code: defects.registering_mach_code,
    worker_id: defects.worker_id,
    failure_count: defects.failure_count, // 不良発生回数
    detection_timing_id: defects.detection_timing_id, // 発見位置、加工前か加工後か
    defect_type_seq: defects.defect_type_seq, // branch_id
    scrap_qty: defects.scrap_qty, // 不良枚数
    occurred_proc_code: defects.occurred_proc_code, // 発生加工コード
    occurred_mach_code: defects.occurred_mach_code, // 発生機械作業コード
    defect_type_id: defects.defect_type_id, // 不良品理由区分
    root_cause: root_cause, // 発生原因
    corrective_action: corrective_action, // 対策
    update_by: userId, // DBの桁数少ないため暫定処置
  });

  return postDefectsData;
};

// 入力チェック
const validateDefects = (
  dispatch: Dispatch<AnyAction>,
  root_cause: string,
  corrective_action: string,
  comment: string
) => {
  const defectDetailErrors: string[] = [];
  let isError = false;
  if (comment === "") {
    defectDetailErrors.push("コメントを入力してください。");
  }
  if (root_cause === "") {
    defectDetailErrors.push("発生原因を入力してください。");
  }
  if (corrective_action === "") {
    defectDetailErrors.push("対策を入力してください。");
  }
  // 発生原因 500byte以内
  if (new Blob([root_cause]).size > 500) {
    defectDetailErrors.push("発生原因は500byte以内で入力してください。");
  }
  // 対策 500byte以内
  if (new Blob([corrective_action]).size > 500) {
    defectDetailErrors.push("対策は500byte以内で入力してください。");
  }
  if (defectDetailErrors.length > 0) {
    isError = true;
    dispatch(setInputErrors(defectDetailErrors));
    dispatch(
      appendAlert({
        severity: "error",
        message: "入力項目を確認してください。",
        displayTime: 5000,
      })
    );
  }
  return isError;
};

export {
  setDefectsList,
  setReviewComment,
  initApproval,
  setRootCause,
  setCorrectiveAction,
  fetchDefects,
  postDefectsReportReviews,
};
export default defectsListSlice.reducer;
